import React, { HTMLAttributes, useEffect, useRef, useState } from "react";

interface IBaseSelect {
  options?: { id: string; display_name: string }[];
  value: string;
  onChange: (v: string) => void;
  id?: string;
  name?: string;
  placeholder?: string;
}

// omit native onChange so our onChange(v: string) doesn't conflict
type TBaseSelectProps = IBaseSelect &
  Omit<HTMLAttributes<HTMLDivElement>, "onChange">;

const BaseSelect: React.FC<TBaseSelectProps> = ({
  options = [],
  value,
  onChange,
  id,
  name,
  placeholder,
  ...otherProps
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selected = options.find((o) => o.id === value);

  return (
    <div className="relative" ref={ref} {...otherProps}>
      <button
        id={id}
        name={name}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((s) => !s)}
        className="w-full border bg-mainDark rounded-lg px-3 py-2 text-left text-white placeholder-gray-400 flex items-center justify-between"
      >
        <span className={`${selected ? "text-white" : "text-gray-400"}`}>
          {selected?.display_name || placeholder}
        </span>
        <svg
          className="w-4 h-4 text-gray-300 ml-2"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6 8l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <ul
          role="listbox"
          tabIndex={-1}
          className="absolute z-40 mt-2 w-full bg-[#0b1220] border border-gray-700/60 rounded-lg max-h-56 overflow-auto"
        >
          <li
            role="option"
            onClick={() => {
              onChange("");
              setOpen(false);
            }}
            className="px-3 py-2 text-sm text-gray-300 hover:bg-gray-800 cursor-pointer"
          >
            未指定停車場
          </li>

          {options.map((o) => (
            <li
              key={o.id}
              role="option"
              onClick={() => {
                onChange(o.id);
                setOpen(false);
              }}
              className="px-3 py-2 text-sm text-gray-300 hover:bg-gray-800 cursor-pointer"
            >
              {o.display_name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default BaseSelect;
